import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  serverTimestamp,
  runTransaction,
  increment,
  orderBy, // ✅ ADD
  limit,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { getTodayIST } from "../utils/dateUtils";
import { getNextTokenNumber } from "../utils/tokenCounter";

const VISIT_TYPE_HELPER = {
  NEW: "This is your first visit to VPJC for consultation or treatment.",
  REVISIT: "You have visited VPJC before and coming back after a long gap.",
  REVIEW:
    "You visited recently and are returning for a doctor review or report discussion.",
  THERAPY: "You are coming for a scheduled or ongoing therapy session",
};

// ===== CLINIC LOCATIONS =====
const CLINIC_LOCATIONS = [
  {
    name: "Gajuwaka Branch",
    lat: 17.6799221,
    lng: 83.1987568,
  },
  {
    name: "Kurmannapalem Branch",
    lat: 17.6862913,
    lng: 83.165883,
  },
];

const ALLOWED_RADIUS_METERS = 300; // you can adjust later
function getDistanceInMeters(lat1, lng1, lat2, lng2) {
  const toRad = (value) => (value * Math.PI) / 180;

  const R = 6371000; // Earth radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export default function TokenRegister() {
  const location = useLocation();
const navigate = useNavigate();

const registrationFlow = Boolean(
  location.state?.registrationFlow
);

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [errors, setErrors] = useState({});
  const [visitingFor, setVisitingFor] = useState("");
  const [visitingForOther, setVisitingForOther] = useState("");
  const [visitType, setVisitType] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  //Location
  const [locationError, setLocationError] = useState("");
  const [checkingLocation, setCheckingLocation] = useState(false);

  const [isSuggestedVisitType, setIsSuggestedVisitType] = useState(false);
  const [visitMode, setVisitMode] = useState("");

  const [checkingPatient, setCheckingPatient] = useState(false);
  const [patientFound, setPatientFound] = useState(false);
  const [matchedPatients, setMatchedPatients] = useState([]);
  const [mobileError, setMobileError] = useState("");
  const [notFoundError, setNotFoundError] = useState("");
  const matchedPatientsBackup = useRef([]);

  const handleBack = () => {
    // 1️⃣ From result → back to form
    if (result) {
      setResult(null);
      return;
    }

    // 2️⃣ From form → back to patient selection (MULTI MATCH CASE)
    if (patientFound && matchedPatientsBackup.current.length > 0) {
      setPatientFound(false);
      setMatchedPatients(matchedPatientsBackup.current);
      return;
    }

    // 3️⃣ From patient selection → back to mobile input
    if (matchedPatients.length > 0) {
      setMatchedPatients([]);
      return;
    }

    // 4️⃣ From mobile → back to visit mode
    if (visitMode) {
      setVisitMode("");
      setMobile("");
      setName("");
    }
  };

  const checkExistingPatient = async () => {
    // 🔒 Mobile validation
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setMobileError("Please enter a valid 10-digit mobile number");
      return;
    }

    setCheckingPatient(true);
    setMobileError("");
    setNotFoundError("");

    try {
      const q = query(
        collection(db, "patients"),
        where("mobile", "==", mobile),
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        // ❌ NOT FOUND
        setNotFoundError("We couldn't find any patient with this number.");
        setCheckingPatient(false);
        return;
      }

      // 🔥 MULTIPLE PATIENTS (ONLY >1)
      if (snap.docs.length > 1) {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setMatchedPatients(list);
        matchedPatientsBackup.current = list;
        setPatientFound(false);
      }
      // 🔥 SINGLE PATIENT (AUTO SELECT)
      else {
        const p = snap.docs[0].data();
        setName(p.name);
        setPatientFound(true);
        setMatchedPatients([]); // IMPORTANT: clear list
        setVisitingFor("SELF");
      }
    } catch (err) {
      console.error("Patient lookup error:", err);
      setMobileError("Error fetching patient. Try again.");
    }

    setCheckingPatient(false);
  };

  useEffect(() => {
  if (!registrationFlow) return;

  const draft = location.state?.registrationDraft;

  if (!draft) return;

  setName(draft.name || "");
  setMobile(draft.mobile || "");
  setVisitMode("NEW");
  setVisitType("NEW");
  setVisitingFor("SELF");
}, [registrationFlow]);

  useEffect(() => {
    if (visitMode === "OLD" && visitType === "NEW") {
      setVisitType(""); // ❌ remove New Consultation
    }

    if (visitMode === "NEW") {
      setVisitType("NEW"); // ✅ force new consultation
    }
  }, [visitMode]);

  const normalizeName = (name) => {
    return name.trim().toLowerCase();
  };

  const generateRandomNumericVPJCId = () => {
    const num = Math.floor(100000 + Math.random() * 900000); // 6 digit number
    return `VPJC-${num}`;
  };

  const validate = () => {
    const newErrors = {};

    if (!mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(mobile.trim())) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
    }

    if (!name.trim()) {
      newErrors.name = "Patient name is required";
    }

    if (!visitingFor) {
      newErrors.visitingFor = "Please select visiting for";
    }

    if (visitingFor === "OTHER" && !visitingForOther.trim()) {
      newErrors.visitingForOther = "Please specify visiting for";
    }

    if (!visitType && !isSuggestedVisitType) {
      newErrors.visitType = "Please select visit type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("GPS is not supported on this device");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          let matchedBranch = null;

          for (const clinic of CLINIC_LOCATIONS) {
            const distance = getDistanceInMeters(
              latitude,
              longitude,
              clinic.lat,
              clinic.lng,
            );

            if (distance <= ALLOWED_RADIUS_METERS) {
              matchedBranch = clinic;
              break;
            }
          }

          if (matchedBranch) {
            resolve(matchedBranch); // ✅ RETURN BRANCH
          } else {
            reject(
              "Token generation is available only at the clinic location.",
            );
          }
        },
        () => reject("Location permission denied"),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  };

  const suggestVisitType = async (patientId) => {
    const q = query(
      collection(db, "visits"),
      where("patient_id", "==", patientId),
      orderBy("created_at", "desc"),
      limit(1),
    );

    const snap = await getDocs(q);

    if (snap.empty) return "NEW";

    const lastVisit = snap.docs[0].data().visit_date;
    const lastDate = new Date(lastVisit);
    const today = new Date();

    const diffDays = (today - lastDate) / (1000 * 60 * 60 * 24);

    if (diffDays <= 7) return "REVIEW";
    if (diffDays <= 90) return "THERAPY";
    return "REVISIT";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setCheckingLocation(true);
    setLocationError("");

    let selectedBranch;

    // 1️⃣ Detect branch using GPS
    try {
      selectedBranch = await checkUserLocation();
    } catch (err) {
      setCheckingLocation(false);
      setLocationError(err);
      return;
    }

    setCheckingLocation(false);
    setLoading(true);

    try {
      const today = getTodayIST();
      const normalizedName = normalizeName(name);

      // 2️⃣ Find or create patient
      let vpjcId = "";
      let patientDocId = "";

      const patientQuery = query(
        collection(db, "patients"),
        where("mobile", "==", mobile),
        where("normalized_name", "==", normalizedName),
      );

      const patientSnap = await getDocs(patientQuery);

      if (!patientSnap.empty) {
        const patientData = patientSnap.docs[0].data();
        vpjcId = patientData.vpjc_id;
        patientDocId = patientSnap.docs[0].id;

        await updateDoc(doc(db, "patients", patientDocId), {
          branch_name: selectedBranch.name,
        });
      } else {
        let unique = false;

        while (!unique) {
          const randomId = generateRandomNumericVPJCId();

          const checkQuery = query(
            collection(db, "patients"),
            where("vpjc_id", "==", randomId),
          );

          const checkSnap = await getDocs(checkQuery);

          if (checkSnap.empty) {
            vpjcId = randomId;
            unique = true;
          }
        }

        const patientRef = await addDoc(collection(db, "patients"), {
          vpjc_id: vpjcId,
          name,
          normalized_name: normalizedName,
          mobile,
          visit_count: 0, // ✅ initialize
          referred_by: referredBy || "",
          branch_name: selectedBranch.name,
          created_at: serverTimestamp(),
        });

        patientDocId = patientRef.id;
      }
      let finalVisitType = visitType;
      if (!finalVisitType) {
        finalVisitType = await suggestVisitType(patientDocId);
        setVisitType(finalVisitType);
        setIsSuggestedVisitType(true);
      } else {
        setIsSuggestedVisitType(false);
      }

      const finalVisitingFor =
        visitingFor === "OTHER" ? visitingForOther.trim() : visitingFor;

      // 4️⃣ ATOMIC: counter + visit (SINGLE TRANSACTION)
      const branchKey = selectedBranch.name
        .trim()
        .replace(/\s+/g, "_")
        .toLowerCase();

      const counterRef = doc(db, "counters", `visits_${branchKey}_${today}`);
      const visitsCol = collection(db, "visits");

      let tokenNumber = 0;
      let isFreeReview = false;

      // Check previous visit
      if (finalVisitType === "REVIEW") {
        const lastVisitQuery = query(
          collection(db, "visits"),
          where("patient_id", "==", patientDocId),
        );

        const lastVisitSnap = await getDocs(lastVisitQuery);

        if (!lastVisitSnap.empty) {
          const lastVisitDate = new Date(
            lastVisitSnap.docs[0].data().visit_date,
          );

          const todayDate = new Date(today);

          const diffDays = Math.floor(
            (todayDate - lastVisitDate) / (1000 * 60 * 60 * 24),
          );

          if (diffDays <= 7) {
            isFreeReview = true;
          }
        }
      }
      const visitRef = doc(collection(db, "visits"));

      await runTransaction(db, async (transaction) => {
        const counterSnap = await transaction.get(counterRef);
        const patientRef = doc(db, "patients", patientDocId);

        // 🔥 CRITICAL: FETCH PATIENT INSIDE TRANSACTION (LIKE ADMIN)
        const patientDocSnap = await transaction.get(patientRef);
        const p = patientDocSnap.exists() ? patientDocSnap.data() : null;

        // 🔥 SAME LOGIC AS ADMIN PANEL (NOW BOTH WILL MATCH)
        let incrementPackage = false;
        const isTreatment = p?.treatment_active === true;

        if (
          p &&
          p.active_package &&
          p.package_total_days &&
          (p.package_used_days || 0) < p.package_total_days &&
          finalVisitType === "THERAPY" // clinic-safe
        ) {
          incrementPackage = true;
        }

        const currentVisitCount = patientDocSnap.exists()
          ? p.visit_count || 0
          : 0;

        // 🔢 TOKEN COUNTER
        if (!counterSnap.exists()) {
          tokenNumber = 1;
          transaction.set(counterRef, { last_token: 1 });
        } else {
          tokenNumber = counterSnap.data().last_token + 1;
          transaction.update(counterRef, {
            last_token: tokenNumber,
          });
        }

        // const visitRef = doc(collection(db, "visits"));
        let usedDays = p?.package_used_days || 0;

        if (incrementPackage) {
          usedDays = usedDays + 1;
        }

        transaction.set(visitRef, {
          patient_id: patientDocId,
          vpjc_id: vpjcId,
          name,
          mobile,
          visit_count: currentVisitCount + 1,
          visit_date: today,
          token_number: tokenNumber,
          branch_name: selectedBranch.name,
          created_by: "USER",
          visiting_for: finalVisitingFor,
          visit_type: finalVisitType,
          referred_by: referredBy || "",
          // 🔥 SAME AS ADMIN
          treatment: isTreatment
            ? p?.current_treatment_name || "Treatment"
            : "",

          visit_category: isTreatment
            ? "TREATMENT"
            : incrementPackage
              ? "PACKAGE"
              : "CONSULTATION",

          consultation_payment_status: isTreatment
            ? "TREATMENT"
            : incrementPackage
              ? "PACKAGE"
              : isFreeReview
                ? "FREE_REVIEW"
                : "UNPAID",

          consultation_fee: isFreeReview ? 0 : null,

          treatment_amount: isTreatment ? 0 : null,
          treatment_payment_status: isTreatment ? "UNPAID" : null,

          package_amount: incrementPackage ? p?.package_amount || 0 : 0,
          used_from_package: incrementPackage,
          package_id: incrementPackage ? p?.active_package_id || null : null,

          package_used_snapshot: incrementPackage ? usedDays : null, // ⭐ FIX

          created_at: serverTimestamp(),
        });

        // 🔥 AUTO UPDATE PACKAGE USAGE (VERY IMPORTANT)
        if (incrementPackage && p) {
          const newUsedDays = (p.package_used_days || 0) + 1;
          const isExpired = newUsedDays >= p.package_total_days;

          transaction.update(patientRef, {
            package_used_days: newUsedDays,
            active_package: !isExpired,
            package_expired_at: isExpired ? serverTimestamp() : null,
          });

          // ⭐ PACKAGE COMPLETED UPDATE
          if (isExpired && p.active_package_id) {
            const packageRef = doc(db, "packages", p.active_package_id);

            transaction.update(packageRef, {
              status: "COMPLETED",
              completed_at: serverTimestamp(),
            });
          }
        }

        // 👤 UPDATE VISIT COUNT
        transaction.update(patientRef, {
          visit_count: increment(1),
        });
      });

      // 5️⃣ Show result ONLY after transaction success
     const token = `T-${String(tokenNumber).padStart(3, "0")}`;

      const tokenResult = {
        token,
        vpjcId,
        name,
        mobile,
        visitId: visitRef.id,
        tokenNumber,
        branchName: selectedBranch.name,
        visitingFor: finalVisitingFor,
        visitType: finalVisitType,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        date: new Date().toLocaleDateString(),
        tokenDate: today,
      };

      if (registrationFlow) {
        navigate(location.state.returnPath, {
          replace: true,
          state: {
            tokenRegistrationResult: tokenResult,
            registrationDraft: location.state.registrationDraft,
          },
        });
      } else {
        setResult(tokenResult);
      }

      setReferredBy("");
    } catch (error) {
      console.error("❌ TOKEN ERROR:", error);
      alert("Error generating token. Please try again.");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-grow">
        {/* ===== ATTRACTIVE HEADER ===== */}
        <div className="relative bg-[#204080] pt-6 pb-24 text-center overflow-visible">
          <h1 className="text-lg font-bold text-white">
            Visakha Physiotherapy & Jasti Chiropractic
          </h1>
          <p className="text-sm text-blue-100 mt-1">
            Restoring Health, Reviving Life
          </p>

          {/* Floating Logo */}
          <div className="absolute left-1/2 top-full -translate-x-1/2 -translate-y-12 z-20">
            <div className="bg-white w-20 h-20 rounded-full shadow-xl  flex items-center justify-center">
              <img
                src="/VPJCLogo-New.png"
                alt="VPJC Logo"
                className="w-12 h-12 object-contain"
              />
            </div>
          </div>

          {/* Curve */}
          <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-10">
            <svg
              viewBox="0 0 500 60"
              preserveAspectRatio="none"
              className="w-full h-12"
            >
              <path
                d="M0,0 C150,60 350,60 500,0 L500,60 L0,60 Z"
                fill="#f8fafc"
              />
            </svg>
          </div>
        </div>

        {/* ===== CARD ===== */}
        <div className="flex justify-center px-4 mt-24 pb-24">
          <div className="w-full max-w-xl">
            {!result ? (
              <>
                {/* Title */}
                {!result &&
                  (visitMode || patientFound || matchedPatients.length > 0) && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="mb-6 text-sm text-[#204080] font-medium flex items-center gap-1"
                    >
                      ← Back
                    </button>
                  )}

                <div className="text-center mb-10">
                  <h2 className="text-2xl font-semibold text-[#204080]">
                    Patient Token Registration
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Smart Check-Ins for Better Care.
                  </p>
                </div>
                {!visitMode && (
                  <div className="text-center space-y-6">
                    <h2 className="text-xl font-semibold text-[#204080]">
                      Have you visited VPJC before?
                    </h2>

                    <button
                      onClick={() => {
                        setVisitMode("OLD");
                        setVisitingFor("SELF"); // ✅ force self
                        setVisitType(""); // reset visit type
                      }}
                      className="w-full rounded-full border border-[#204080] py-2"
                    >
                      Yes, I have visited before
                    </button>

                    <button
                      onClick={() => {
                        setVisitMode("NEW");
                        setVisitingFor(""); // user chooses
                        setVisitType("NEW"); // auto new consultation
                      }}
                      className="w-full rounded-full bg-[#204080] text-white py-2"
                    >
                      No, this is my first visit
                    </button>
                  </div>
                )}
                {/* STEP 2: OLD PATIENT – MOBILE CHECK */}
                {visitMode === "OLD" && !patientFound && (
                  <div className="space-y-6">
                    <div className="relative">
                      <input
                        type="tel"
                        value={mobile}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 10) {
                            setMobile(value);
                            setMobileError("");
                            setNotFoundError("");
                          }
                        }}
                        placeholder=" "
                        className="peer w-full bg-transparent border-b-2 py-2 focus:outline-none border-gray-300 focus:border-[#204080]"
                      />
                      <label
                        className="pointer-events-none absolute left-0 transition-all
    top-[-0.5rem]
    text-sm
    peer-placeholder-shown:top-2
    peer-placeholder-shown:text-base
    peer-placeholder-shown:text-black
    peer-focus:top-[-0.5rem]
    peer-focus:text-sm
    peer-focus:text-[#204080]"
                      >
                        Enter Mobile Number
                      </label>
                    </div>
                    {/* Mobile validation error */}
                    {mobileError && (
                      <p className="text-xs text-red-600 mt-1">{mobileError}</p>
                    )}

                    {/* Not found error */}
                    {notFoundError && (
                      <div className="mt-3 space-y-2 text-center">
                        <p className="text-xs text-red-600">{notFoundError}</p>

                        <button
                          type="button"
                          onClick={() => {
                            // ✅ Switch to NEW PATIENT with prefills
                            setVisitMode("NEW");
                            setVisitType("NEW");
                            setVisitingFor("SELF");
                            setNotFoundError("");
                          }}
                          className="text-sm font-semibold text-[#204080]"
                        >
                          Continue as New Patient
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={checkExistingPatient}
                      disabled={checkingPatient}
                      className="w-full rounded-full bg-[#204080] text-white py-2"
                    >
                      {checkingPatient ? "Checking..." : "Find My Details"}
                    </button>
                  </div>
                )}
                {/* STEP 3: MULTIPLE PATIENTS FOUND */}
                {matchedPatients.length > 0 && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 text-center mt-4">
                      We found multiple patients with this number. Please select
                      who is visiting today.
                    </p>

                    {matchedPatients.map((p) => (
                      <button
                        key={p.vpjc_id}
                        type="button"
                        onClick={() => {
                          setName(p.name);
                          setPatientFound(true);
                          setMatchedPatients([]);
                        }}
                        className="w-full border rounded-lg p-3 text-left"
                      >
                        <p className="font-medium">{p.name}</p>
                        <p className="text-xs text-gray-500">{p.vpjc_id}</p>
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={() => {
                        setVisitMode("NEW");
                        setMatchedPatients([]);
                      }}
                      className="w-full text-sm text-[#204080] font-semibold"
                    >
                      + Add New Patient
                    </button>
                  </div>
                )}

                {(visitMode === "NEW" || patientFound) && (
                  <form onSubmit={handleSubmit} className="space-y-10">
                    <div className="relative">
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value.replace(/[^a-zA-Z\s]/g, ""));
                          setErrors((p) => ({ ...p, name: "" }));
                        }}
                        placeholder=" "
                        className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
      ${
        errors.name
          ? "border-red-500 focus:border-red-500"
          : "border-gray-300 focus:border-[#204080]"
      }`}
                      />

                      <label
                        className={`pointer-events-none absolute left-0 transition-all
    top-[-0.5rem]
    text-sm
    peer-placeholder-shown:top-2
    peer-placeholder-shown:text-base
    peer-placeholder-shown:text-black
    peer-focus:top-[-0.5rem]
    peer-focus:text-sm
    peer-focus:text-[#204080]
    ${errors.name ? "text-red-500" : ""}
  `}
                      >
                        Patient Name *
                      </label>

                      <p className="min-h-[16px] text-xs text-red-500 mt-1">
                        {errors.name || ""}
                      </p>
                    </div>

                    {/* Mobile */}
                    <div className="relative">
                      <input
                        type="tel"
                        value={mobile}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 10) {
                            setMobile(value);
                            setErrors((p) => ({ ...p, mobile: "" }));
                          }
                        }}
                        placeholder=" "
                        className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
      ${
        errors.mobile
          ? "border-red-500 focus:border-red-500"
          : "border-gray-300 focus:border-[#204080]"
      }`}
                      />

                      <label
                        className={`pointer-events-none absolute left-0 transition-all
    top-[-0.5rem]
    text-sm
    peer-placeholder-shown:top-2
    peer-placeholder-shown:text-base
    peer-placeholder-shown:text-black
    peer-focus:top-[-0.5rem]
    peer-focus:text-sm
    peer-focus:text-[#204080]
    ${errors.mobile ? "text-red-500" : ""}
  `}
                      >
                        Mobile Number *
                      </label>

                      <p className="min-h-[16px] text-xs text-red-500 mt-1">
                        {errors.mobile || ""}
                      </p>
                    </div>

                    {/* Visiting For */}
                    <div>
                      <select
                        value={visitingFor}
                        onChange={(e) => {
                          setVisitingFor(e.target.value);
                          setVisitingForOther("");
                          setErrors((p) => ({ ...p, visitingFor: "" }));
                        }}
                        disabled={visitMode === "OLD"} // 🔒 lock for old patients
                        className={`w-full bg-transparent border-b-2 py-2 focus:outline-none
    ${
      errors.visitingFor
        ? "border-red-500"
        : "border-gray-300 focus:border-[#204080]"
    }
    ${visitMode === "OLD" ? "opacity-60 cursor-not-allowed" : ""}
  `}
                      >
                        <option value="">Visiting For *</option>

                        {/* OLD PATIENT → SELF ONLY */}
                        {visitMode === "OLD" && (
                          <option value="SELF">Self</option>
                        )}

                        {/* NEW PATIENT → ALL OPTIONS */}
                        {visitMode === "NEW" && (
                          <>
                            <option value="SELF">Self</option>
                            <option value="CHILD">Child</option>
                            <option value="FAMILY">Family Member</option>
                            <option value="OTHER">Other</option>
                          </>
                        )}
                      </select>

                      <p className="min-h-[16px] text-xs text-red-500 mt-1">
                        {errors.visitingFor || ""}
                      </p>

                      {/* Show input only when Other is selected */}
                      {visitingFor === "OTHER" && (
                        <div className="relative mt-4">
                          <input
                            type="text"
                            value={visitingForOther}
                            onChange={(e) => {
                              setVisitingForOther(e.target.value);
                              setErrors((p) => ({
                                ...p,
                                visitingForOther: "",
                              }));
                            }}
                            placeholder=" "
                            className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
          ${
            errors.visitingForOther
              ? "border-red-500 focus:border-red-500"
              : "border-gray-300 focus:border-[#204080]"
          }`}
                          />

                          <label
                            className={`pointer-events-none absolute left-0 transition-all
          top-[-0.5rem]
          text-sm
          peer-placeholder-shown:top-2
          peer-placeholder-shown:text-base
          peer-placeholder-shown:text-black
          peer-focus:top-[-0.5rem]
          peer-focus:text-sm
          peer-focus:text-[#204080]
          ${errors.visitingForOther ? "text-red-500" : ""}
        `}
                          >
                            Specify Other *
                          </label>

                          <p className="min-h-[16px] text-xs text-red-500 mt-1">
                            {errors.visitingForOther || ""}
                          </p>
                        </div>
                      )}
                    </div>
                    {/* Referred By */}
                    {visitType === "NEW" && (
                  <div className="relative">
                    <input
                      type="text"
                      value={referredBy}
                      onChange={(e) => setReferredBy(e.target.value)}
                      placeholder=" "
                      className="peer w-full bg-transparent border-b-2 py-2 focus:outline-none"
                    />

                    <label
                      className="pointer-events-none absolute left-0 transition-all
      top-[-0.5rem]
      text-sm
      peer-placeholder-shown:top-2
      peer-placeholder-shown:text-base
      peer-placeholder-shown:text-black
      peer-focus:top-[-0.5rem]
      peer-focus:text-sm
      peer-focus:text-[#204080]"
                    >
                      Referred By
                    </label>
                  </div>
                )}
                    {/* Visit Type */}
                    <div>
                      <select
                        value={visitType}
                        onChange={(e) => {
                          setVisitType(e.target.value);
                          setErrors((p) => ({ ...p, visitType: "" }));
                        }}
                        className={`w-full bg-transparent border-b-2 py-2 focus:outline-none
    ${
      errors.visitType
        ? "border-red-500"
        : "border-gray-300 focus:border-[#204080]"
    }`}
                      >
                        <option value="">Type of Visit *</option>

                        {/* NEW PATIENT → ONLY New Consultation */}
                        {visitMode === "NEW" && (
                          <option value="NEW">New Consultation</option>
                        )}

                        {/* OLD PATIENT → NO New Consultation */}
                        {visitMode === "OLD" && (
                          <>
                            <option value="REVISIT">Revisit</option>
                            <option value="REVIEW">Review / Follow-up</option>
                            <option value="THERAPY">Therapy Session</option>
                          </>
                        )}
                      </select>

                      {isSuggestedVisitType && (
                        <p className="mt-1 text-xs text-blue-600">
                          Suggested based on your last visit
                        </p>
                      )}

                      <p className="mt-2 text-xs text-gray-500">
                        {visitType
                          ? VISIT_TYPE_HELPER[visitType]
                          : "Select the purpose of your visit today"}
                      </p>

                      <p className="min-h-[16px] text-xs text-red-500 mt-1">
                        {errors.visitType || ""}
                      </p>
                    </div>
                    {locationError && (
                      <p className="text-xs text-red-600 text-center mt-2">
                        {locationError}
                      </p>
                    )}

                    {/* Button */}
                    <button
                      type="submit"
                      disabled={loading || checkingLocation}
                      className="w-full rounded-full bg-[#204080] text-white
             py-2 text-sm font-semibold
             disabled:opacity-60"
                    >
                      {checkingLocation
                        ? "Checking Location..."
                        : loading
                          ? "Generating Token..."
                          : "Get Token"}
                    </button>
                  </form>
                )}
              </>
            ) : (
              /* RESULT */
              <div className="text-center space-y-8">
                {/* Success Indicator */}
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#204080]/10 mx-auto">
                  <svg
                    className="w-6 h-6 text-[#204080]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                {/* Token */}
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400">
                    Your Token Number
                  </p>
                  <h1 className="mt-2 text-6xl font-extrabold tracking-tight text-[#204080]">
                    {result.token}
                  </h1>
                </div>

                {/* Details */}
                <div className="max-w-sm mx-auto divide-y divide-gray-200 text-sm">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">VPJC ID</span>
                    <span className="font-medium">{result.vpjcId}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Patient</span>
                    <span className="font-medium">{result.name}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Visit Type</span>
                    <span className="font-medium">{result.visitType}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Date</span>
                    <span className="font-medium">{result.date}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Time</span>
                    <span className="font-medium">{result.time}</span>
                  </div>
                </div>

                {/* Instruction */}
                <p className="text-sm font-semibold text-[#204080]">
                  Please show this at the reception desk
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <footer className="mt-auto bg-[#204080] text-white">
        <div className="py-4 text-center text-[11px] sm:text-xs px-4">
          © {new Date().getFullYear()} VPJC ·{" "}
          <a
            href="https://www.charvionglobal.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium hover:text-blue-200 transition"
          >
            Powered by CGS
          </a>
        </div>
      </footer>
    </div>
  );
}
